import {useCallback, useMemo} from "react";
import {AvatarGroup, Card, CardContent, CardHeader, Grid, Link, Stack, Typography} from "@mui/material";
import {useFloorStore} from "../../api/FloorStore";
import {useDeskStore} from "../../api/DeskStore";
import {RemoteValue, useRemoteValue} from "../../util/RemoteValue";
import {LoadingData} from "../../components/loading-data/LoadingData";
import {Page, StorePageCount} from "../../components/page/Page";
import {AppPages, usePageUrl} from "../../app-layout/AppPages";
import {FloorModel} from "./FloorModel";
import {FloorPlanImage} from "./FloorPlanImage";
import {useDeskModelStore} from "../../api/DeskModelStore";
import {DateRangeOptions} from "../../components/filter-fields/DateRangeFilter";
import NeighbourhoodColorIcon from "../../components/neighbourhood-icon/NeighbourhoodColorIcon";
import EmployeeAvatar from "../../components/employee-avatar/EmployeeAvatar";
import {useFilter} from "../../util/FilterSerializer";
import {filterFloorSummaries, FloorsFilter, floorsFilterSerializer} from "./FloorsFilter";
import {useNeighbourhoodStore} from "../../api/NeighbourhoodStore";
import {Link as RouterLink} from "react-router-dom";

export function FloorSummaries({floorSummaries, floorUrl, selectFloor}: {
    floorSummaries: readonly FloorModel[],
} & ({
    floorUrl: (floor: FloorModel) => string,
    selectFloor?: never,
} | {
    floorUrl?: never,
    selectFloor: (floor: FloorModel) => void,
})) {

    return (
        <Stack direction="column" spacing={3}>
            {floorSummaries.map(floorModel => (
                <Card key={floorModel.floor.id} sx={{display: 'flex', flexDirection: 'column'}}>
                    <Grid container>
                        <Grid size={6}>
                            <CardHeader
                                title={
                                    floorUrl ? (
                                        <Link
                                            component={RouterLink}
                                            to={floorUrl(floorModel)}
                                            underline="hover"
                                        >
                                            {floorModel.floor.name}
                                        </Link>
                                    ) : (
                                        <Link
                                            component="button"
                                            type="button"
                                            onClick={() => selectFloor(floorModel)}
                                            underline="hover"
                                            sx={{font: 'inherit', textAlign: 'inherit'}}
                                        >
                                            {floorModel.floor.name}
                                        </Link>
                                    )
                                }
                                subheader={
                                    <Stack direction="row" alignItems="center" gap={4}>
                                        <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
                                            {floorModel.floorEmployees.length} {floorModel.floorEmployees.length === 1 ? 'employee' : 'employees'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
                                            {floorModel.floorDesks.length} {floorModel.floorDesks.length === 1 ? 'desk' : 'desks'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
                                            {floorModel.floorNeighbourhoods.length} {floorModel.floorNeighbourhoods.length === 1 ? 'neighbourhood' : 'neighbourhoods'}
                                        </Typography>
                                    </Stack>
                                }
                            />
                            {floorModel.floorEmployees.length > 0 && (
                                <CardContent>
                                    <AvatarGroup
                                        max={10}
                                        sx={{width: 'fit-content'}}
                                        slotProps={{
                                            additionalAvatar: {
                                                sx: {
                                                    width: '32px',
                                                    height: '32px',
                                                    fontSize: '14px'
                                                }
                                            }
                                        }}
                                    >
                                        {floorModel.floorEmployees.map(employee => (
                                            <EmployeeAvatar key={employee.id} employee={employee}
                                                            sx={{width: '32px', height: '32px'}}/>
                                        ))}
                                    </AvatarGroup>
                                </CardContent>
                            )}
                            {floorModel.floorNeighbourhoods.length > 0 && (
                                <CardContent>
                                    <Stack direction="row"
                                           flexWrap="wrap"
                                           aria-label={`Neighbourhoods on ${floorModel.floor.name}`}
                                           columnGap={2}
                                    >{floorModel.floorNeighbourhoods.map(neighbourhood => (
                                        <Stack key={neighbourhood.key}
                                               direction="row"
                                               gap={1}
                                               alignItems="center"
                                        >
                                            <NeighbourhoodColorIcon neighbourhood={neighbourhood} size={16}/>
                                            <Typography variant="body2">
                                                {neighbourhood.name}
                                            </Typography>
                                        </Stack>
                                    ))}</Stack>
                                </CardContent>
                            )}
                        </Grid>
                        <Grid size={6}>
                            <CardContent>
                                <FloorPlanImage
                                    src={floorModel.floor.floorPlanUrl}
                                    alt={`Floor plan for ${floorModel.floor.name}`}
                                    compact
                                />
                            </CardContent>
                        </Grid>
                    </Grid>
                </Card>
            ))}
        </Stack>
    );
}

export default function FloorsPage() {
    const floors = useFloorStore(state => state.remoteValue);
    const desks = useDeskStore(state => state.remoteValue);
    const neighbourhoods = useNeighbourhoodStore(state => state.remoteValue);
    const resolvedNeighbourhoods = useRemoteValue(neighbourhoods, []);
    const filterSerializer = useMemo(
        () => floorsFilterSerializer(resolvedNeighbourhoods),
        [resolvedNeighbourhoods],
    );
    const pageUrl = usePageUrl();
    const floorUrl = useCallback((floorModel: FloorModel) =>
            pageUrl(AppPages.floor, {floorId: floorModel.floor.id}),
        [pageUrl]);
    const [filter, setFilter] = useFilter(filterSerializer);
    const deskModel = useDeskModelStore(DateRangeOptions.lastFourMonths);
    const unfilteredFloorModels = useMemo(
        () => RemoteValue
            .combine({deskModel, desks})
            .map(({deskModel, desks}) => {
                return FloorModel.allFloors(deskModel, desks);
            }),
        [deskModel, desks],
    );
    const floorModels = useMemo(
        () => unfilteredFloorModels.map(rows => filterFloorSummaries(rows, filter)),
        [unfilteredFloorModels, filter],
    );

    return (
        <Page
            header="Floors"
            counter={<StorePageCount totalValue={floors} value={floorModels}/>}
            description="Office floors, desks, neighbourhoods, and floor plans."
            filter={
                <FloorsFilter
                    filter={filter}
                    setFilter={setFilter}
                    allNeighbourhoods={resolvedNeighbourhoods}
                />
            }
        >
            <LoadingData
                totalValue={unfilteredFloorModels}
                value={floorModels}
                emptyMessage="No floors found."
                noMatchMessage="No floors match the current filters. Try adjusting your filter criteria."
                defaultErrorMessage="Failed to load floors. Please try again later."
            >{floorSummaries => (
                <FloorSummaries
                    floorSummaries={floorSummaries}
                    floorUrl={floorUrl}
                />
            )}</LoadingData>
        </Page>
    );
}
